import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { ForgotPasswordDialog, Input } from '../components';
import { forgottenPasswordDialogAtom } from '../atoms';
import { LoginSchema } from '../lib/schemas';
import { PATHS } from '../lib/paths';
import { useLoginMutation, useValidateSessionMutation } from '../lib/queries';

const defaultValues = {
  email: '',
  password: '',
};

export default function LoginPage() {
  const { t } = useTranslation();
  const [, setForgottenPasswordDialogState] = useAtom(forgottenPasswordDialogAtom);
  const [apiError, setApiError] = useState('');
  const navigate = useNavigate();
  const { register, handleSubmit, formState } = useForm({
    mode: 'all',
    defaultValues,
    resolver: zodResolver(LoginSchema),
  });
  const validateSession = useValidateSessionMutation();
  const loginMutation = useLoginMutation();

  const onSubmit = async (data: typeof defaultValues) => {
    try {
      await loginMutation.mutateAsync(data);
      await validateSession.mutateAsync();
      navigate(PATHS.homePath());
    } catch (error: any) {
      setApiError(t(`apiError.${error.message}`));
    }
  };

  return (
    <main className="grid place-items-center h-full !m-0">
      <form
        method="post"
        className="grid gap-2 w-72 mx-auto p-4 rounded"
        onSubmit={handleSubmit(onSubmit)}
      >
        <h2 className="text-title font-semibold mb-10">{t('login.title')}</h2>
        {apiError && (
          <span className="grid items-center p-4 rounded bg-danger-500 bg-opacity-5 text-danger-500">
            {apiError}
          </span>
        )}
        <Input
          type="text"
          {...register('email', { required: true })}
          label={t('common.email')}
          placeholder="email"
          error={formState.errors.email?.message}
          required
        />
        <Input
          type="password"
          {...register('password', { required: true })}
          label={t('common.password')}
          placeholder="password"
          error={formState.errors.password?.message}
          required
        />
        <span
          className="text-label ml-auto hover:text-primary-500 font-semibold cursor-pointer"
          onClick={() => setForgottenPasswordDialogState((prev) => ({ ...prev, isOpen: true }))}
        >
          {t('forgotPassword.forgottenPassword')}
        </span>
        <button disabled={!formState.isValid} className="primary-fill" type="submit">
          {t('login.title')}
        </button>
      </form>
      <ForgotPasswordDialog />
    </main>
  );
}
